import Vue from 'vue';

const FACEBOOK_APP_ID = process.env.VUE_APP_FACEBOOK_APP_ID || '';
const GRAPH_API_VERSION = 'v21.0';

const AUTH_ORIGIN = process.env.VUE_APP_FACEBOOK_AUTH_ORIGIN || 'https://www.auth.studioconnect.fr';
const REDIRECT_URI = `${AUTH_ORIGIN}/auth/facebook/callback`;

// Le backend répond en HTTP 200 même en cas d'échec ({ status: 'error', message }) :
// on transforme ce refus en exception pour que l'écran affiche la vraie cause.
async function req(method, url, data) {
    const rep = await Vue.prototype.$srvApi.Req(method, url, data);
    if (!rep || rep.status !== 'success')
        throw new Error((rep && rep.message) || 'Erreur serveur Meta');
    return rep;
}

class FacebookLeadsService {
    constructor() {
        this._popupInterval = null;
    }

    /**
     * Connexion Facebook via popup OAuth
     * @returns {Promise<string>} access_token court
     */
    login() {
        return new Promise((resolve, reject) => {
            if (!FACEBOOK_APP_ID || FACEBOOK_APP_ID === 'VOTRE_APP_ID') {
                reject(new Error(
                    'Facebook App ID non configuré. Modifiez VUE_APP_FACEBOOK_APP_ID dans .env'
                ));
                return;
            }

            // Doit rester aligné avec api_core/app/meta/graph.js.
            // `pages_read_engagement` est exigé par Meta pour lire les leads et fait
            // partie des permissions qui autorisent l'usage d'un page access token
            // (cf. message d'erreur 190 « ...before impersonating a user's page »).
            // `pages_manage_ads` sert au backfill par API (GET /{form_id}/leads) :
            // Meta l'ignore tant qu'elle n'est pas approuvée, donc la demander ne
            // casse rien.
            //
            // Volontairement ABSENTES : ads_management, ads_read, business_management.
            // Le cas d'usage Meta en dépend au niveau de l'app, mais le flux runtime
            // ne lit que id/created_time/field_data — aucune donnée publicitaire. Les
            // faire apparaître dans le consentement client alourdirait le dialogue
            // sans contrepartie. Les appels de test exigés par la review se font avec
            // un token admin : voir api_core/tools/meta-review-calls.js.
            const permissions = [
                'pages_show_list',
                'pages_read_engagement',
                'pages_manage_metadata',
                'leads_retrieval',
                'pages_manage_ads'
            ].join(',');

            // L'origine du tenant est transmise via `state` pour que le callback
            // centralisé sache vers quel sous-domaine relayer le token.
            const tenantOrigin = window.location.origin;

            const authUrl = `https://www.facebook.com/${GRAPH_API_VERSION}/dialog/oauth?` +
                `client_id=${FACEBOOK_APP_ID}` +
                `&redirect_uri=${encodeURIComponent(REDIRECT_URI)}` +
                `&response_type=token` +
                `&scope=${permissions}` +
                // Force Facebook à redemander les permissions précédemment refusées
                // (sinon une permission décochée comme leads_retrieval n'est jamais re-proposée).
                `&auth_type=rerequest` +
                `&state=${encodeURIComponent(tenantOrigin)}`;

            const width = 600, height = 700;
            const left = (window.screen.width - width) / 2;
            const top = (window.screen.height - height) / 2;

            const popup = window.open(
                authUrl,
                'facebook-login',
                `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes`
            );

            if (!popup) {
                reject(new Error('Impossible d\'ouvrir la popup. Vérifiez que les popups ne sont pas bloquées.'));
                return;
            }

            const messageHandler = (event) => {
                // Le message provient désormais du domaine d'authentification centralisé.
                if (event.origin !== AUTH_ORIGIN) return;
                if (event.data && event.data.type === 'facebook-auth') {
                    window.removeEventListener('message', messageHandler);
                    clearInterval(this._popupInterval);
                    if (event.data.accessToken) {
                        resolve(event.data.accessToken);
                    } else {
                        reject(new Error(event.data.error || 'Réponse inattendue de Facebook'));
                    }
                }
            };

            window.addEventListener('message', messageHandler);

            this._popupInterval = setInterval(() => {
                if (popup.closed) {
                    clearInterval(this._popupInterval);
                    window.removeEventListener('message', messageHandler);
                    reject(new Error('Connexion annulée'));
                }
            }, 500);
        });
    }

    /**
     * Échanger le token court contre un token longue durée (via backend)
     */
    async getLongLivedToken(shortLivedToken) {
        const response = await req('post', 'meta/auth/exchange-token', {
            short_lived_token: shortLivedToken
        });
        return response.data.access_token;
    }

    /**
     * Récupérer les pages Facebook (via backend)
     */
    async getPages(userAccessToken) {
        const response = await req('post', 'meta/auth/pages', {
            access_token: userAccessToken
        });
        return response.data || [];
    }

    /**
     * Auditer les permissions réellement accordées (via backend).
     * Meta retire silencieusement une permission refusée par l'utilisateur ou
     * non approuvée pour l'app : sans ce contrôle, une connexion morte
     * s'affiche comme réussie et les prospects n'arrivent jamais.
     */
    async checkPermissions(userAccessToken) {
        const response = await req('post', 'meta/auth/permissions', {
            access_token: userAccessToken
        });
        return response.data || null;
    }
}

const facebookLeadsService = new FacebookLeadsService();
export default facebookLeadsService;
